import supabase from './supabaseUtils';
import { loadTemplate, getAllBlocksInScene } from './cesdkUtils';
import { addWatermark } from './imageUtils';

export const exportTemplateCustomized = async (cesdk, templateId, replacementData) => {
  if (!cesdk?.engine?.scene) return null;
  try {
    // Ensure the template is loaded
    const loaded = await loadTemplate(cesdk, templateId);
    if (!loaded) throw new Error('Failed to load template');

    const scene = cesdk.engine.scene.get();
    if (!scene) throw new Error('No scene');

    const blocks = getAllBlocksInScene(cesdk, scene);

    for (const [key, value] of Object.entries(replacementData)) {
      const blockId = blocks.find(b => cesdk.engine.block.getName(b) === key);
      if (!blockId) continue;
      const type = cesdk.engine.block.getType(blockId);

      if (type === '//ly.img.ubq/text') {
        cesdk.engine.block.setString(blockId, 'text/text', value);
      } else if (type === '//ly.img.ubq/graphic' && value.startsWith('http')) {
        // --- BEGIN: Improved image fill logic for 'Person' and similar placeholders ---
        let fillId = cesdk.engine.block.getFill(blockId);
        if (!fillId) {
          fillId = cesdk.engine.block.createFill('image');
          cesdk.engine.block.setFill(blockId, fillId);
          cesdk.engine.block.setFillEnabled(blockId, true);
        }
        await new Promise(r => setTimeout(r, 50));
        try {
          // Use legacy imageFileURI to preserve natural aspect ratio
          cesdk.engine.block.setString(fillId, 'fill/image/imageFileURI', value);
        } catch (e) {
          console.warn(`Failed to set image for block ${blockId}:`, e.message);
          continue;
        }
                 // Set image properties using CESDK 1.52.0 content fill mode (on the block, not the fill)
         try {
           // CESDK 1.52.0: Use contentFill/mode 'Fit' to match template creation settings
           cesdk.engine.block.setEnum(blockId, 'contentFill/mode', 'Fit');
           console.log(`Set contentFill/mode to 'Fit' for block ${blockId}`);
         } catch (e) {
           console.log(`Could not set contentFill/mode for block ${blockId} - using default`);
         }
        // --- END: Improved image fill logic ---
      }
    }

    const exportBlob = await cesdk.engine.block.export(scene, 'image/png', {
      targetWidth: 1080,
      targetHeight: 1920,
      dpi: 72,
      exportArea: 'design'
    });

    // Store to Supabase (same path logic)
    const { data: session } = await supabase.auth.getSession();
    const userPath = session?.session?.user?.id || 'anonymous';
    const exportFileName = `${templateId}-export-${Date.now()}.png`;
    const watermarkedFileName = `${templateId}-export-watermarked-${Date.now()}.png`;

    // Upload original export
    const { error } = await supabase.storage.from('playmakerpics-images').upload(`exports/${userPath}/${exportFileName}`, exportBlob, {
      upsert: true,
      cacheControl: 'no-cache',
      contentType: 'image/png'
    });
    if (error) throw error;

    const publicUrl = supabase.storage
      .from('playmakerpics-images')
      .getPublicUrl(`exports/${userPath}/${exportFileName}`).data.publicUrl;

    // Create and upload minimally watermarked version (quality focused)
    let watermarkedUrl = null;
    try {
      let watermarkedBlob = exportBlob;
      
      // Watermark 1: Top corner brand
      watermarkedBlob = await addWatermark(watermarkedBlob, {
        text: "PlayMaker Pics",
        position: 3, // Top right
        opacity: 0.8,
        fontSize: "48px", // Doubled from 24px
        color: "rgba(0, 0, 0, 0.9)" // Dark/black for prominence
      });
      
      // Watermark 2: Center subtle brand
      watermarkedBlob = await addWatermark(watermarkedBlob, {
        text: "PlayMaker Pics",
        position: 5, // Center
        opacity: 0.4,
        fontSize: "72px", // Doubled from 36px
        color: "rgba(0, 0, 0, 0.7)" // Dark but still subtle
      });
      
      // Legal claim at bottom
      watermarkedBlob = await addWatermark(watermarkedBlob, {
        text: "© PlayMaker Pics - All Rights Reserved",
        position: 8, // Bottom center
        opacity: 0.9,
        fontSize: "32px", // Doubled from 16px
        color: "rgba(0, 0, 0, 0.9)" // Dark for clear legal notice
      });

      const { error: watermarkError } = await supabase.storage
        .from('playmakerpics-images')
        .upload(`exports/${userPath}/${watermarkedFileName}`, watermarkedBlob, {
          upsert: true,
          cacheControl: 'no-cache',
          contentType: 'image/png'
        });

      if (!watermarkError) {
        watermarkedUrl = supabase.storage
          .from('playmakerpics-images')
          .getPublicUrl(`exports/${userPath}/${watermarkedFileName}`).data.publicUrl;
      } else {
        console.warn('Failed to upload watermarked version:', watermarkError);
      }
    } catch (watermarkError) {
      console.warn('Failed to create watermarked version:', watermarkError);
    }

    // Record this export in the database so it appears in /designs and other dashboards
    try {
      await supabase.from('exports').insert({
        user_id: session?.session?.user?.id || null,
        template_id: templateId,
        export_url: publicUrl,
        export_url_wm: watermarkedUrl, // Store watermarked URL
        exported_at: new Date().toISOString(),
        status: 'active',
        is_saved: true // directly mark as saved so it shows up
      });
    } catch (dbErr) {
      console.warn('Could not insert export record:', dbErr);
    }

    // Return watermarked URL for display, fallback to original if watermarking failed
    return (watermarkedUrl || publicUrl) + `?t=${Date.now()}`;
  } catch (err) {
    console.error('exportTemplateCustomized failed', err);
    return null;
  }
}; 